import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";

import { useBoardUsers } from "../../../../reactQuery/users";
import Avatar from "../../../../components/Avatar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createAvatarName, hasImage } from "../../../../utils";
import { assignCard } from "../../../../services/apiCards";
import NotAssignCardIcon from "../../../../styles/svg/unassigned.svg";
import FormDialog from "../../../../components/Dialog/FormDialog";
import { getAPIBaseUrl } from "../../../../utils/constants";
import CommentForm from "../../../../components/CommentPopUp";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import assignedTasks from "../../../../styles/svg/assignedTasks.svg";
import AssignedCardMenu from "./mobileKanban/AssignedCardMenu";

import useToastPromise from "../../../../hooks/useToast";
import CustomMenu from "../../../../components/Menu";
import CustomMenuItem from "../../../../components/Menu/CustomMenuItem";
import useBoardIdByKey from "../../../../hooks/useBoardIdByKey";

function AssignCard({
  tooltipName,
  cardId,
  name,
  project,
  assigneeOptions = null,
  accessForAssign,
  callback: refetch,
  isAssignable = true,
  className = "",
  isShowAssignedCards = false,
  hasReassignPermission = false,
  accessForQuickTask = false,
  isTodo,
}) {
  const { id: boardId } = useBoardIdByKey();

  const id = boardId || project?.board_id;
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);
  const getUrl = getAPIBaseUrl();
  const { isLoading, error, data, isError } = useBoardUsers(id);
  const [workflowId] = useCustomSearchParams("workflow_id");
  const [openAssignedCardMenu, setOpenAssignedCardMenu] = useState(null);
  const { showToastPromise } = useToastPromise();
  const [openWorkload, setOpenWorkload] = useState(false);

  useEffect(() => {
    const sourceUsers = assigneeOptions || data?.data?.data?.responseData || [];
    const sortedUsers = [...sourceUsers].sort(function (a, b) {
      return a?.username?.localeCompare(b?.username);
    });

    const updatedUsers = sortedUsers?.reduce((acc, user) => {
      if (user?.user_id === project?.assigned_to_user_id) {
        // Place the assigned user at the beginning of the array
        acc.unshift(user);
      } else {
        acc.push(user);
      }
      return acc;
    }, []);

    const activeUsers = updatedUsers?.filter((user) => user?.active) || [];
    setUsers(activeUsers);
  }, [assigneeOptions, data, id, isLoading, project?.assigned_to_user_id]);

  const avatarStyle = useMemo(() => {
    if (hasImage(project?.assigned_to_user?.user_image)) {
      return undefined;
    }

    return {
      bgcolor: project?.assigned_to_user?.profile_color?.background,
      color: project?.assigned_to_user?.profile_color?.text,
    };
  }, [
    project?.assigned_to_user?.profile_color?.background,
    project?.assigned_to_user?.profile_color?.text,
    project?.assigned_to_user?.user_image,
  ]);

  const handleClick = (event) => {
    event.stopPropagation();
    if (isTodo ? accessForQuickTask : accessForAssign)
      setAnchorEl(event?.currentTarget);
  };

  const handleClose = (event) => {
    event?.stopPropagation();
    setAnchorEl(null);
    setOpenAssignedCardMenu(null);
  };

  const { mutate: assignCardMutate } = useMutation({
    mutationFn: async (data) => {
      return await showToastPromise(assignCard, data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["boards"],
      });
      queryClient.invalidateQueries({
        queryKey: [`cards`, id],
      });
      queryClient.invalidateQueries({
        queryKey: [`card`, cardId],
      });
      queryClient.invalidateQueries({
        queryKey: [`history`, String(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: [`comment`, String(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: [`watching`, workflowId],
      });
      queryClient.invalidateQueries({
        queryKey: [`userCards`],
      }); 
      queryClient.invalidateQueries({
        queryKey: [`todos`],
      });

      setUserId(null);
      setOpenAssignedCardMenu(null);
      setLoading(false);
      setOpenDialog(false);
    },
    onError: (error) => {
      console.error("Error assig board", error);
      setUserId(null);
      setOpenAssignedCardMenu(null);
      setLoading(false);
      setOpenDialog(false);
    },
  });

  const assignUser = (id) => {
    setOpenDialog(true);
    setOpenAssignedCardMenu(null);
    setUserId(id);
  };

  const assignCallback = (event) => {
    // event.stopPropagation();
    setLoading(true);
    assignCardMutate({
      card_id: project?.card_id,
      data: {},
    });
  };

  const closeConfirmation = (event) => {
    event.stopPropagation();
    setOpenDialog(false);
    setUserId(null);
    setOpenAssignedCardMenu(null);
    handleClose();
  };

  const handleAssignedTasks = (event, id) => {
    event.stopPropagation();
    setUserId(id);
    setOpenAssignedCardMenu(event.currentTarget);
  };
  const handleAssignedCardClose = (event) => {
    event?.stopPropagation();
    setOpenAssignedCardMenu(null);
    setUserId(null);
  };

  const handleMouseOut = () => {
    setOpenWorkload(!openWorkload);
  };

  return (
    <div>
      {userId?.user_id === "null" && openDialog && (
        <FormDialog
          open={openDialog}
          handleClose={closeConfirmation}
          loading={loading}
          handleSubmit={assignCallback}
          buttonTxt="Unassign"
          title="Confirmation: Unassign task"
          alignActions={"center"}
          cancelBtn={true}
          fullWidth={true}>
          <Typography className="align_text_center mb-2">
            Are you sure you want to unassigned this task
          </Typography>
        </FormDialog>
      )}
      {openDialog && userId?.user_id !== "null" && (
        <FormDialog
          open={openDialog}
          handleClose={closeConfirmation}
          title={`Add a Note for ${userId?.username}`}
          width="657px">
          <CommentForm
            cardId={project?.card_id}
            userId={userId?.user_id}
            boardId={id}
            onClose={() => {
              handleClose();
              setOpenDialog(false);
              setUserId(null);
              setOpenAssignedCardMenu(null);
            }}
            workflow_id={workflowId}
            parentTaskCardId={
              project?.parent_card_id && project?.parent_card_id
            }
          />
        </FormDialog>
      )}
      <div onClick={handleClick}>
        {project?.assigned_to_user_id ? (
          <Avatar
            title={tooltipName}
            className={`card-avtar header-avatar ${className}`}
            user_image={project?.assigned_to_user?.user_image}
            style={avatarStyle}>
            {name}
          </Avatar>
        ) : (
          <Avatar
            unassigned={true}
            title={"Unassigned"}
            className={`card-avtar header-avatar ${className}`}
            user_image={NotAssignCardIcon}
          />
        )}
      </div>
      {isAssignable && (
        <>
          <CustomMenu
            id="account-menu"
            anchorEl={anchorEl}
            // open={open}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 0,
              className: "assign-card-users-list",
              onMouseLeave: handleMouseOut,
            }}
            className={`assign-board-owner-list ${!openDialog && (openWorkload ? "" : "z-1301")}`}
          >
            <CustomMenuItem
              onClick={() =>
                assignUser({
                  user_id: "null",
                  username: "unassigned",
                })
              }>
              None
            </CustomMenuItem>
            {users?.map((el) => {
              const convertedImage = getUrl + el.user_image;
              return (
                <CustomMenuItem
                  key={`user-avtar-dropdown-card-assign-${el?.user_id}`}
                  onClick={(event) => {
                    // event.stopPropagation();
                    if (el.user_id !== project?.assigned_to_user_id) {
                      assignUser(el);
                    } else {
                      event.stopPropagation();
                    }
                  }}
                  className={`d-flex justify-content-space-between user-menu-item ${
                    project?.assigned_to_user_id &&
                    project.assigned_to_user_id == el.user_id
                      ? "active-assign"
                      : ""
                  }`}>
                  <Box className="d-flex align-items-center ">
                    <Avatar
                      key={`user-avtar-${el?.user_id}`}
                      username={el?.username}
                      user_image={el?.user_image}
                      title={el?.username}
                      className="user-avtar-dropdown user-avatar mr-8"
                      style={
                        !hasImage(el?.user_image) && {
                          bgcolor: el?.profile_color?.background,
                          color: el?.profile_color?.text,
                        }
                      }>
                      {createAvatarName(el?.username)}
                    </Avatar>

                    {el?.username}
                  </Box>
                  {isShowAssignedCards && (
                    <IconButton
                      onMouseOver={(event) => {
                        handleAssignedTasks(event, el);
                      }}
                      className={`user-show-assigned-task-icon ${
                        el?.user_id === userId?.user_id
                          ? "show-assingned-icon"
                          : ""
                      }`}>
                      <img src={assignedTasks} alt="assigned Tasks to user" />
                    </IconButton>
                  )}
                </CustomMenuItem>
              );
            })}
          </CustomMenu>
          {
            (openAssignedCardMenu && openAssignedCardMenu !== null && isShowAssignedCards && userId?.user_id && !openDialog) && (
              <AssignedCardMenu openAssignedTask={openAssignedCardMenu} handleClose={handleAssignedCardClose} cardId={cardId} userId={userId?.user_id} boardId={id} openWorkload={openWorkload} handleMouseOut={handleMouseOut} />
            )
          }
        </>
      )}
    </div>
  );
}

function areAssignCardPropsEqual(prevProps, nextProps) {
  return (
    prevProps.tooltipName === nextProps.tooltipName &&
    prevProps.cardId === nextProps.cardId &&
    prevProps.name === nextProps.name &&
    prevProps.accessForAssign === nextProps.accessForAssign &&
    prevProps.isAssignable === nextProps.isAssignable &&
    prevProps.className === nextProps.className &&
    prevProps.isShowAssignedCards === nextProps.isShowAssignedCards &&
    prevProps.hasReassignPermission === nextProps.hasReassignPermission &&
    prevProps.accessForQuickTask === nextProps.accessForQuickTask &&
    prevProps.isTodo === nextProps.isTodo &&
    prevProps.project?.card_id === nextProps.project?.card_id &&
    prevProps.project?.board_id === nextProps.project?.board_id &&
    prevProps.project?.assigned_to_user_id ===
      nextProps.project?.assigned_to_user_id &&
    prevProps.project?.reporter === nextProps.project?.reporter &&
    prevProps.project?.todo_column === nextProps.project?.todo_column &&
    prevProps.project?.assigned_to_user?.user_image ===
      nextProps.project?.assigned_to_user?.user_image &&
    prevProps.project?.assigned_to_user?.first_name ===
      nextProps.project?.assigned_to_user?.first_name &&
    prevProps.project?.assigned_to_user?.last_name ===
      nextProps.project?.assigned_to_user?.last_name &&
    prevProps.project?.assigned_to_user?.profile_color?.background ===
      nextProps.project?.assigned_to_user?.profile_color?.background &&
    prevProps.project?.assigned_to_user?.profile_color?.text ===
      nextProps.project?.assigned_to_user?.profile_color?.text &&
    JSON.stringify(prevProps.assigneeOptions ?? null) ===
      JSON.stringify(nextProps.assigneeOptions ?? null)
  );
}

export default React.memo(AssignCard, areAssignCardPropsEqual);
